import { resolveSlug } from "@/config/serviceCatalog";
import type { ServiceModule } from "./types";

/** Lazy-loaded service module registry. Only registered services have custom UI. */
const registry: Record<string, () => Promise<{ default: ServiceModule }>> = {
  "fasadnicke-prace": () => import("./fasady"),
};

const cache: Record<string, ServiceModule> = {};

/**
 * Load a service module by engine_id. Returns null for unknown/generic services.
 * Results are cached — safe to call repeatedly.
 */
export async function getServiceModule(engineId: string | null | undefined): Promise<ServiceModule | null> {
  if (!engineId) return null;
  const normalizedSlug = resolveSlug(engineId);
  if (!registry[normalizedSlug]) return null;
  if (cache[normalizedSlug]) return cache[normalizedSlug];
  try {
    const mod = await registry[normalizedSlug]();
    cache[normalizedSlug] = mod.default;
    return mod.default;
  } catch (e) {
    console.error(`Failed to load service module "${normalizedSlug}":`, e);
    return null;
  }
}

/** Check if a service has a registered module (synchronous, no loading). */
export function hasServiceModule(engineId: string | null | undefined): boolean {
  return !!engineId && !!registry[resolveSlug(engineId)];
}

/** Get cached module synchronously. Returns null if not yet loaded. */
export function getServiceModuleSync(engineId: string | null | undefined): ServiceModule | null {
  if (!engineId) return null;
  return cache[resolveSlug(engineId)] || null;
}
